import * as Yup from 'yup'

export const OTHER_REGULATORY_APPROVAL_MAX_LENGTH = 255

export const maxCharsMessage = (label: string, maxLength: number): string =>
    `${label} must be ${maxLength} characters or less`

const requiredString = (label: string) =>
    Yup.string()
        .trim()
        .required()
        .label(label)

const requiredNumber = (label: string) =>
    Yup.number()
        .required()
        .label(label)

const requiredArray = (label: string, min = 1) =>
    Yup.array()
        .of(Yup.string())
        .min(min, `At least ${min} ${label} must be selected`)
        .required()

export const prefeasibilityStageSchema = {

    project_status_and_readiness: Yup.object({
        current_development_phase: requiredString('Current Development Phase'),
        readiness_for_procurement: requiredString('Readiness for Procurement'),
        regulatory_approvals_status: requiredArray('Regulatory Approvals Status'),
        other_regulatory_approval: Yup.string()
            .trim()
            .max(
                OTHER_REGULATORY_APPROVAL_MAX_LENGTH,
                maxCharsMessage('Other regulatory approval', OTHER_REGULATORY_APPROVAL_MAX_LENGTH)
            ),
        stakeholder_engagement_status: requiredString('Stakeholder Engagement Status'),
        procurement_readiness: requiredString('Procurement Readiness'),
        regulatory_readiness: requiredString('Regulatory Readiness'),
        stakeholder_readiness: requiredString('Stakeholder Readiness'),
    }),

    governance_and_compliance: Yup.object({
        policy_alignment: requiredArray('Policy Alignment'),
        policy_alignment_rationale: Yup.string().when('policy_alignment', {
            is: (val: string[]) => Array.isArray(val) && val.length > 0,
            then: (schema) => schema.trim().required('Policy Alignment Rationale is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        legal_compliance_status: requiredString('Legal Compliance Status'),
        implementation_model: requiredString('Implementation Model'),
        delivery_incentives_penalties: requiredString('Incentives & Penalties to Support Successful Delivery'),
    }),

    procurement_readiness: Yup.object({
        bfi_alternative_financing_model_id: Yup.number()
        .required("BFI Alternative Financing Model ID is required")
        .min(1, "BFI Alternative Financing Model ID is required"),
        supplier_market_maturity: requiredString('Supplier Market Maturity'),
        risk_sharing_mechanism: requiredArray('Risk Sharing Mechanism'),
        budget_statement: requiredString('Budget Statement'),
        procurement_procedure: requiredString('Procurement Procedure'),
        procurement_plan: requiredString('Procurement Plan'),
    }),

    implementation_readiness: Yup.object({

        imple_timeline_planning_start: Yup.date()
            .required("Planning start is required"),

        imple_timeline_planning_end: Yup.date()
            .required("Planning end is required")
            .min(
                Yup.ref("imple_timeline_planning_start"),
                "Planning end must be after planning start"
            ),

        imple_timeline_procurement_start: Yup.date()
            .required("Procurement start is required")
            .min(
                Yup.ref("imple_timeline_planning_end"),
                "Procurement start must not be before planning end"
            ),

        imple_timeline_procurement_end: Yup.date()
            .required("Procurement end is required")
            .min(
                Yup.ref("imple_timeline_procurement_start"),
                "Procurement end must be after procurement start"
            ),

        imple_timeline_delivery_start: Yup.date()
            .required("Delivery start is required")
            .min(
                Yup.ref("imple_timeline_procurement_end"),
                "Delivery start must not be before procurement end"
            ),

        imple_timeline_delivery_end: Yup.date()
            .required("Delivery end is required")
            .min(
                Yup.ref("imple_timeline_delivery_start"),
                "Delivery end must be after delivery start"
            ),

        imple_timeline_operational_start: Yup.date()
            .required("Operational start is required")
            .min(
                Yup.ref("imple_timeline_delivery_end"),
                "Operational start must not be before delivery end"
            ),

        imple_timeline_operational_end: Yup.date()
            .required("Operational end is required")
            .min(
                Yup.ref("imple_timeline_operational_start"),
                "Operational end must be after operational start"
            ),

        key_constraints: requiredArray('Constraint', 1),

        delivery_model_confidence_score: requiredNumber('Delivery Model Confidence Score'),
    }),

    stakeholder_and_communication_readiness: Yup.object({
        investor_engagement_status: requiredString('Investor Engagement Status'),
        community_participation_status: requiredString('Community Participation Status'),
        government_coordination_level: requiredString('Government Coordination Level'),
    }),

    risk_and_quality_gate: Yup.object({}),
    governance_policy_enabling_environment: Yup.object({
        government_policies_governance_role: requiredString('Government Policies Governance Role'),
        governance_structure: requiredString('Governance Structure'),
        internal_controls_accountability: requiredString('Internal Controls Accountability'),
        agreements_dispute_resolution: requiredString('Agreements Dispute Resolution'),
        incentives_penalties_framework: requiredString('Incentives & Penalties Framework'),
        national_policy_alignment: requiredString('National Policy Alignment'),
        regulatory_compliance_frameworks: requiredArray('Regulatory Compliance Frameworks'),
        policy_alignment_summary_bfi_consistency: requiredString('Policy Alignment Summary BFI Consistency'),
    }),
    economic_and_commercial_viability: Yup.object({
        value_for_money_assessment: requiredString('Value-for-Money Assessment'),
        benefit_analysis: requiredString('Benefit Analysis'),
        risk_evaluation_and_contingent_liability: requiredString('Risk Evaluation & Contingent Liability'),
        economic_multipliers_and_macroeconomic_benefits: requiredString('Economic Multipliers & Macroeconomic Benefits'),
        economies_of_scale: requiredString('Economies of Scale'),
    }),

    documents: Yup.object({}),

}

export type IPrefeasibilityStageSchema =
    keyof typeof prefeasibilityStageSchema